import { action, computed, observable } from 'mobx';
import Collection from 'core/model/Collection';
import { api } from 'core/util/index';
import CustomDimensionModel from './CustomDimensionModel';
import AsGroupModel, { AS_GROUP_DIMENSIONS } from './AsGroupModel';

class AsGroupCollection extends Collection {
  @observable
  dimensions = {};

  get url() {
    return '';
  }

  get model() {
    return AsGroupModel;
  }

  @computed
  get populated() {
    return this.get().length > 0;
  }

  @action
  fetch = (options) => {
    let needRefresh = false;
    this.setRequestStatus('fetching');
    this.hasFetched = true;
    return Promise.all(
      AS_GROUP_DIMENSIONS.map(({ name, displayName }) =>
        CustomDimensionModel.fetchByName(name)
          .then((dimension) => {
            if (!dimension) {
              const newDimension = new CustomDimensionModel();
              needRefresh = true;
              return newDimension
                .create(
                  { display_name: displayName, name },
                  { toast: false, url: `${newDimension.urlRoot}?skipSync=1` }
                )
                .then(() => newDimension);
            }

            return dimension;
          })
          .then((dimension) => {
            this.dimensions[name] = dimension;
            dimension.populators.setSortState('id');
            return dimension.populators.fetch(options).then(() => dimension.populators);
          })
      )
    ).then(
      action(async ([srcPopulators, dstPopulators]) => {
        // get a map of the group name to the ASNs
        const groupMap = new Map([
          ...srcPopulators.map((model) => [model.get('value'), model.get('asn')]),
          ...dstPopulators.map((model) => [model.get('value'), model.get('asn')])
        ]);

        // find the corresponding populators for each group, and build any missing ones
        const groupPopulators = Array.from(groupMap).map(([value, asn]) => {
          const src = srcPopulators.find({ value }) || srcPopulators.forge({ value, asn, direction: 'src' });
          const dst = dstPopulators.find({ value }) || dstPopulators.forge({ value, asn, direction: 'dst' });
          return [src, dst];
        });

        // save any missing populators
        const newPopulators = groupPopulators.flat().filter((populator) => populator.isNew);
        await Promise.all(newPopulators.map((populator) => populator.save({}, { toast: false })));

        const groupData = groupPopulators.map(([src, dst]) => AsGroupModel.getData(src, dst));

        this.set(groupData);
        this.setRequestStatus(null);

        if (needRefresh) {
          await api.get('/api/ui/customdimensions/rebuildDevices');
        }
      })
    );
  };
}

export default AsGroupCollection;
